/**
 * The STRUCTURED risk rows a report contributes to deterministic scoring.
 *
 * Shared by the composite's finding-severity penalty
 * (`pillarScorer.findingSeverityPenaltyForPillar`) and the Decision Guide's
 * PATH 6 critical-finding cap (`executiveDecisionRollup`), so the two engines
 * read ONE definition of "a structured, unmitigated risk" and cannot drift —
 * previously each carried its own copy of the mitigation-placeholder rule with
 * a comment asking the other to stay in sync.
 *
 * WHY THE ENVIRONMENTAL RISK MATRIX IS PROJECTED HERE (P1.7, 2026-09-05).
 * `report.risks[]` is populated only for feasibility and supply-chain reports;
 * the environmental report's structured risk output lives in
 * `environmentalRiskMatrix[]`, whose `severity` is an unconstrained string that
 * nothing parsed. So an environmental report could carry a critical,
 * unmitigated "take permit unobtainable" row and neither cap the call (PATH 6
 * needs `severity: 'critical'` on `risks[]`) nor cost the environmental pillar
 * a point — the "weigh findings, not completion" failure, on the one report
 * whose whole purpose is naming such findings.
 *
 * ADMISSIBILITY. The doctrine admits LLM-authored rows only as STRUCTURED
 * evidence: `RiskItem.severity` is schema-constrained, which is what lets it
 * carry weight. The matrix severity is not schema-constrained, so this
 * projection applies the same constraint after the fact — a row is admitted
 * only when its severity is EXACTLY one of the four enum words
 * (case-insensitive, trimmed). "Severe", "H", "4/5" or an empty cell admit
 * nothing: never fabricate exposure from a value the vocabulary does not hold.
 * The rows are projected for SCORING only; they are not written back onto
 * `report.risks`, so no rendered surface, persisted document or semantic
 * analysis changes shape.
 */
import type { RiskCategory, RiskItem, SiteRagReport } from '../../types/siteRag';

/**
 * R4a — is this risk UNMITIGATED?
 *
 * `RiskItem.mitigation` is a required string in the schema, but an LLM may emit
 * an empty or placeholder value. A risk with a stated mitigation is priced work,
 * not open exposure, so only risks with no real mitigation text carry weight.
 * The 3-char floor plus the placeholder pattern reject "-", "n/a", "TBD" filler
 * without attempting to judge the QUALITY of a stated mitigation — that is a
 * human call, and over-reading it here would let prose drive the band.
 */
export function isUnmitigatedRisk(risk: Pick<RiskItem, 'mitigation'>): boolean {
  const mitigation = risk.mitigation?.trim() ?? '';
  if (mitigation.length < 3) return true;
  return /^(n\/?a|none|tbd|unknown)$/i.test(mitigation);
}

const SEVERITY_VOCABULARY: ReadonlyMap<string, RiskItem['severity']> = new Map([
  ['critical', 'critical'],
  ['high', 'high'],
  ['medium', 'medium'],
  ['low', 'low'],
]);

/**
 * Parse a free-text severity into the `RiskItem.severity` enum, or `null` when
 * the text is not exactly one of the four vocabulary words. Strict on purpose
 * (see the module header): unknown vocabulary admits nothing.
 */
export function parseStructuredSeverity(raw: unknown): RiskItem['severity'] | null {
  if (typeof raw !== 'string') return null;
  return SEVERITY_VOCABULARY.get(raw.trim().toLowerCase()) ?? null;
}

/** Stable id prefix for projected matrix rows (index-keyed, so reruns dedupe). */
export const ENVIRONMENTAL_RISK_MATRIX_ID_PREFIX = 'env-matrix-';

/**
 * The environmental report feeds TWO pillars (environmental + regulatory), so
 * the free-text matrix `category` has to discriminate. Permitting / compliance
 * wording routes to `regulatory`; everything else is ecological exposure.
 */
function categoryForEnvironmentalRow(category: unknown): RiskCategory {
  const text = typeof category === 'string' ? category : '';
  return /regulat|permit|complian|nepa|zoning/i.test(text) ? 'regulatory' : 'environmental';
}

/**
 * `environmentalRiskMatrix[]` rows as `RiskItem`s — ONLY the rows whose
 * severity parses. Pure; returns `[]` for any report without a matrix.
 */
export function environmentalRiskMatrixAsRisks(report: SiteRagReport): RiskItem[] {
  const rows = report.environmentalRiskMatrix;
  if (!Array.isArray(rows) || rows.length === 0) return [];
  const out: RiskItem[] = [];
  rows.forEach((row, index) => {
    if (!row || typeof row !== 'object') return;
    const severity = parseStructuredSeverity(row.severity);
    const label = typeof row.risk === 'string' ? row.risk.trim() : '';
    if (!severity || !label) return;
    const residual = typeof row.residualRisk === 'string' ? row.residualRisk.trim() : '';
    out.push({
      id: `${ENVIRONMENTAL_RISK_MATRIX_ID_PREFIX}${index}`,
      category: categoryForEnvironmentalRow(row.category),
      label,
      severity,
      impact: residual ? `Residual risk: ${residual}` : label,
      mitigation: typeof row.mitigation === 'string' ? row.mitigation : '',
      triggeringValue: `environmentalRiskMatrix[${index}]`,
    });
  });
  return out;
}

/**
 * Every structured risk row a report contributes to scoring: the explicit
 * `risks[]` plus the projected environmental matrix rows (deduped on id; an
 * explicit row always wins).
 */
export function scoringRisksForReport(report: SiteRagReport): RiskItem[] {
  const explicit = Array.isArray(report.risks)
    ? report.risks.filter((r): r is RiskItem => r != null && typeof r === 'object')
    : [];
  const projected = environmentalRiskMatrixAsRisks(report);
  if (projected.length === 0) return explicit;
  const ids = new Set(explicit.map((r) => r.id));
  return [...explicit, ...projected.filter((r) => !ids.has(r.id))];
}
